import { useTheme } from '@emotion/react';
import { ImageFiles } from '@icon-park/react';
import cn from 'classnames';
import { memo, useMemo } from 'react';

import { TEACHING_UNIT_ORIGIN } from '@didaskill/domain/courses/constants';
import type { TeachingUnitLessonViewStudent } from '@didaskill/domain/courses/types';
import type { Upload, UploadWithHref } from '@didaskill/domain/uploads/types';
import { Card, Typography } from '@didaskill/ui/design-system';
import { Truncate } from '@didaskill/ui/utils';

import { getLogoForOrigin } from '../../../../assets/external-logos/constants';
import {
  LESSON_CARD_PROGRESS_COLOR_MAPPING,
  LESSON_CARD_VARIANT_MAPPING,
  LESSON_CARD_VARIANTS,
} from './constants';
import { LessonCardFooter } from './footer/LessonCardFooter';
import * as styles from './LessonCard.styles';

export type LessonCardProps = {
  lesson: TeachingUnitLessonViewStudent['lesson'];
  origin?: TeachingUnitLessonViewStudent['origin'];
  thumbnail?: Upload | UploadWithHref;
  completionPercentage?: number;
  isDisabled?: boolean;
  isLocked?: boolean;
  nbRemainingDays?: number;
  sequencesCount?: number;
};

export const LessonCardBase = ({
  lesson,
  origin,
  thumbnail,
  completionPercentage,
  isDisabled = false,
  isLocked = false,
  nbRemainingDays,
  sequencesCount = 0,
}: LessonCardProps) => {
  const theme = useTheme();

  const isCompleted = completionPercentage === 100;

  const isLessonExternal = origin !== TEACHING_UNIT_ORIGIN.DIDASKILL;

  const lessonCardVariant = useMemo(() => {
    if (isLocked) {
      return LESSON_CARD_VARIANTS.LOCKED;
    }

    if (isCompleted) {
      return LESSON_CARD_VARIANTS.COMPLETED;
    }

    if (completionPercentage > 0) {
      return LESSON_CARD_VARIANTS.IN_PROGRESS;
    }

    if (isDisabled) {
      return LESSON_CARD_VARIANTS.DISABLED;
    }

    return LESSON_CARD_VARIANTS.TO_START;
  }, [isCompleted, completionPercentage, isDisabled, isLocked]);

  const colorProgress = LESSON_CARD_PROGRESS_COLOR_MAPPING(theme)[lessonCardVariant];

  return (
    <Card
      color={colorProgress}
      variant={LESSON_CARD_VARIANT_MAPPING[lessonCardVariant]}
      progress={completionPercentage ? completionPercentage / 100 : null}
      css={styles.container}
      className={cn({
        isLocked,
      })}
    >
      <div css={styles.content}>
        <div
          css={styles.thumbnail}
          className={cn('opacity-on-hover', {
            isDisabled,
            isExternal: isLessonExternal,
            isLocked,
          })}
        >
          {thumbnail?.href ? (
            <div css={styles.image(thumbnail?.href)} />
          ) : (
            <div css={styles.placeholderImage}>
              <ImageFiles
                size={48}
                theme="outline"
                fill={theme.colors.primary[500]}
                strokeWidth={1}
              />
            </div>
          )}

          {isLessonExternal && (
            <div css={styles.logoContainer}>{getLogoForOrigin(origin, 'lesson-thumbnail')}</div>
          )}
        </div>

        <Truncate maxLines={2}>
          <Typography
            className={cn({
              isDisabled,
            })}
            css={styles.title}
            variant={Typography.VARIANTS.BODY_SMALL_BOLD}
          >
            {lesson.title}
          </Typography>
        </Truncate>
      </div>

      <LessonCardFooter
        lessonCardVariant={lessonCardVariant}
        isLessonExternal={isLessonExternal}
        progress={completionPercentage}
        sequencesCount={lesson.sequencesCount || sequencesCount}
        nbRemainingDays={nbRemainingDays}
      />
    </Card>
  );
};

LessonCardBase.displayName = 'LessonCard';

export const LessonCard = memo(LessonCardBase);
